import React from "react";
import { Wand2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SectionPanel, Field } from "@/views/gooseberry/shared";
import { DESIGN_PHASES } from "@/data/gooseBerryData";

const copy = {
  nl: {
    title: "Context",
    help: "Helpt GooseBerry begrijpen voor wie en waarvoor je ontwerpt. Verplichte velden zijn gemarkeerd met *.",
    description: "Beschrijf voor wie en wat je ontwerpt, zodat GooseBerry gerichte feedback kan geven.",
    projectName: "Projectnaam",
    targetGroup: "Doelgroep",
    targetHint: "Voor wie ontwerp je dit? Wees zo specifiek mogelijk.",
    targetPlaceholder: "Volwassenen 65+ met chronische pijn",
    societalTheme: "Maatschappelijk thema",
    societalHint: "Een kort thema, bijvoorbeeld digitale inclusie, energie of toegang tot zorg.",
    societalPlaceholder: "Digitale inclusie",
    designPhase: "Ontwerpfase",
    assumptions: "Aannames",
    assumptionsHint: "Dingen die je aanneemt over gebruikers, context of randvoorwaarden, deze worden bevraagd in de output.",
    assumptionsPlaceholder: "Wat neem je aan over je gebruikers, context of randvoorwaarden?",
  },
  en: {
    title: "Context",
    help: "Helps GooseBerry understand who and what you're designing for. Required fields are marked with *.",
    description: "Describe who and what you're designing for, so GooseBerry can give targeted feedback.",
    projectName: "Project name",
    targetGroup: "Target group",
    targetHint: "Who are you designing this for? Be as specific as you can.",
    targetPlaceholder: "Adults 65+ with chronic pain",
    societalTheme: "Societal theme",
    societalHint: "A short topic label, e.g. digital inclusion, energy, healthcare access.",
    societalPlaceholder: "Digital inclusion",
    designPhase: "Design phase",
    assumptions: "Assumptions",
    assumptionsHint: "Things you assume about your users, context, or constraints, these will be questioned in the output.",
    assumptionsPlaceholder: "What do you assume about your users, context, or constraints?",
  },
};

export function ContextPanel({ values, set, language = "en" }) {
  const { projectName, targetGroup, societalTheme, designPhase, assumptions } = values;
  const t = copy[language] || copy.en;

  return (
    <SectionPanel
      number="3"
      title={t.title}
      required
      icon={<Wand2 className="h-4 w-4" />}
      help={t.help}
      description={t.description}
      testid="synberry-context-card"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={t.projectName} required testid="synberry-input-projectName">
          <Input value={projectName} onChange={(e) => set.projectName(e.target.value)} placeholder="MedReminder" className="rounded-xl" data-testid="synberry-input-projectName-field" />
        </Field>
        <Field label={t.targetGroup} required testid="synberry-input-targetGroup" hint={t.targetHint}>
          <Input value={targetGroup} onChange={(e) => set.targetGroup(e.target.value)} placeholder={t.targetPlaceholder} className="rounded-xl" data-testid="synberry-input-targetGroup-field" />
        </Field>
        <Field label={t.societalTheme} optional testid="synberry-input-societalTheme" hint={t.societalHint} className="sm:flex sm:h-full sm:flex-col" labelClassName="sm:min-h-8" controlClassName="sm:mt-auto">
          <Input value={societalTheme} onChange={(e) => set.societalTheme(e.target.value)} placeholder={t.societalPlaceholder} className="rounded-xl" data-testid="synberry-input-societalTheme-field" />
        </Field>
        <Field label={t.designPhase} required testid="synberry-input-designPhase" className="sm:flex sm:h-full sm:flex-col" labelClassName="sm:min-h-8" controlClassName="sm:mt-auto">
          <Select value={designPhase} onValueChange={set.designPhase}>
            <SelectTrigger className="rounded-xl" data-testid="synberry-input-designPhase-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DESIGN_PHASES.map((p) => (
                <SelectItem key={p} value={p} data-testid={`synberry-design-phase-${p}`}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <div className="sm:col-span-2">
          <Field label={t.assumptions} optional testid="synberry-input-assumptions" hint={t.assumptionsHint}>
            <Textarea value={assumptions} onChange={(e) => set.assumptions(e.target.value)} placeholder={t.assumptionsPlaceholder} rows={3} className="rounded-xl" data-testid="synberry-input-assumptions-field" />
          </Field>
        </div>
      </div>
    </SectionPanel>
  );
}
